globalThis.multiselect = globalThis.multiselect || {};
globalThis.multiselect.debug = false;

// Debug helper
globalThis.multiselect.log = function (...args) {
    if (!globalThis.multiselect.debug) return;
    console.log(...args);
};
// End of Debug helper

// Store DotNet refs by UniqueIdentifier
if (!(globalThis.savedDotNetRefs instanceof Map)) {
    globalThis.savedDotNetRefs = new Map();
}

globalThis.registerMultiSelectInstance = function (id, dotNetRef) {
    if (!(globalThis.savedDotNetRefs instanceof Map)) {
        globalThis.savedDotNetRefs = new Map();
    }

    globalThis.savedDotNetRefs.set(id, dotNetRef);
    globalThis.multiselect.log("[registerMultiSelectInstance] registered", id);
};

globalThis.unregisterMultiSelectInstance = function (id) {
    const refs = globalThis.savedDotNetRefs;
    if (!(refs instanceof Map)) return;

    refs.delete(id);
    globalThis.multiselect.log("[unregisterMultiSelectInstance] unregistered", id);
};

// ===== measureBadges =====
globalThis.multiselect.measureBadges = function () {
    const els = document.querySelectorAll(".tagBadgeMeasure");
    globalThis.multiselect.log("[measureBadges] found", els.length, "tagBadgeMeasure elements");

    const out = [];

    els.forEach(el => {
        const key = el.dataset.key;

        const textEl =
            el.querySelector(".badgeText") ||
            el.querySelector(".tagBadges") ||
            el;

        const truncated = textEl.scrollWidth > textEl.clientWidth;

        globalThis.multiselect.log(`[measureBadges] key: ${key}, truncated: ${truncated}`);

        const tooltipAnchor = el.closest(".ebizTooltip");
        if (!truncated && tooltipAnchor) {
            tooltipAnchor.removeAttribute("title");
            globalThis.multiselect.log(`[measureBadges] Removed tooltip title for key: ${key}`);
        }

        if (key) {
            out.push({ key, truncated });
        }
    });

    globalThis.multiselect.log("[measureBadges] result", out);
    return out;
};

// ===== tagsAreWrapped =====
globalThis.multiselect.tagsAreWrapped = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) return false;

    globalThis.multiselect.log("[tagsAreWrapped] called with hostId =", hostId, "host =", host);

    const badges = host.querySelectorAll(".tagBadges");
    if (badges.length <= 1) return false;

    globalThis.multiselect.log("[tagsAreWrapped] badge count =", badges.length);

    const firstTop = badges[0].offsetTop;
    const wrapped = Array.from(badges).some(b => b.offsetTop > firstTop);

    globalThis.multiselect.log("[tagsAreWrapped] wrapped =", wrapped);

    return wrapped;
};

globalThis.multiselect.computeMaxVisibleTags = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) return 1;

    const container = host.querySelector(".k-input-values") || host;
    if (!container) return 1;

    const badges = container.querySelectorAll(".tagBadges");
    if (!badges.length) return 1;

    let summaryWidth = 0;
    const summary = container.querySelector(".summary-tag");

    if (summary) {
        const s = getComputedStyle(summary);
        summaryWidth =
            summary.getBoundingClientRect().width +
            Number.parseFloat(s.marginLeft || 0) +
            Number.parseFloat(s.marginRight || 0);
    } else {
        summaryWidth = 70;
    }

    const containerWidth = container.getBoundingClientRect().width;

    let used = 0;
    let count = 0;

    for (const badge of badges) {
        const st = getComputedStyle(badge);
        const w =
            badge.getBoundingClientRect().width +
            Number.parseFloat(st.marginLeft || 0) +
            Number.parseFloat(st.marginRight || 0);

        // Stop if adding summary tag would wrap
        if (used + w + summaryWidth > containerWidth) break;

        used += w;
        count++;
    }

    return Math.max(1, count);
};

globalThis.multiselect.summaryTagIsWrapped = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) {
        globalThis.multiselect.log("[summaryTagIsWrapped] host not found for", hostId);
        return false;
    }

    const container = host.querySelector(".k-input-values") || host;
    if (!container) {
        globalThis.multiselect.log("[summaryTagIsWrapped] container (.k-input-values) not found");
        return false;
    }

    const badges = container.querySelectorAll(".tagBadges");
    const summary = container.querySelector(".summary-tag");

    globalThis.multiselect.log("[summaryTagIsWrapped] badges.len =", badges.length, "summary =", summary);

    if (!summary || badges.length === 0) {
        return false;
    }

    const firstRect = badges[0].getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();

    const deltaTop = summaryRect.top - firstRect.top;

    // If summary is noticeably lower than the first badge, it's wrapped
    const wrapped = deltaTop > 5;

    globalThis.multiselect.log(
        "[summaryTagIsWrapped] firstTop=", firstRect.top,
        "summaryTop=", summaryRect.top,
        "deltaTop=", deltaTop,
        "wrapped=", wrapped
    );

    return wrapped;
};

// Namespace
globalThis.ebizMultiSelect = globalThis.ebizMultiSelect || {};

globalThis.ebizMultiSelect.clearInput = function (selector) {
    const element = document.querySelector(selector);
    if (!element) return;

    const input = element.querySelector("input.k-input-inner");
    if (input) {
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
    }
};

// ===== AddOnClickHandler helpers =====

// Removes any stored handler from el[storageKey] then attaches the new one.
function _ebizAttachListener(el, event, handler, storageKey, useCapture) {
    if (el[storageKey]) {
        el.removeEventListener(event, el[storageKey], useCapture ?? false);
    }
    el.addEventListener(event, handler, useCapture ?? false);
    el[storageKey] = handler;
}

// Attaches a mousedown preventDefault handler to el (no-op if el is null).
function _ebizAttachMouseDownPrevention(el, storageKey) {
    if (!el) return;
    _ebizAttachListener(el, "mousedown", e => e.preventDefault(), storageKey);
}

// Returns a capture-phase Enter handler that invokes OnEnterKeyDown then scrolls to bottom.
function _ebizCreateEnterKeyDownHandler(id, root) {
    return async function (e) {
        if (e.key !== "Enter") return;
        if (!e.target.value?.trim()) return;

        const refs = globalThis.savedDotNetRefs;
        if (!(refs instanceof Map) || !refs.has(id)) return;

        e.preventDefault();
        e.stopPropagation();

        try {
            await refs.get(id).invokeMethodAsync("OnEnterKeyDown");
            // Wait for Blazor to re-render the new tag before measuring scrollHeight.
            await globalThis.multiselect.afterLayout();
            const ms = root.querySelector(".ebiz-multi-select-dropdown");
            if (ms) ms.scrollTop = ms.scrollHeight;
        } catch (err) {
            globalThis.multiselect.log("[EnterKeyDown] invoke failed", err);
        }
    };
}

// Returns a keydown handler that scrolls to the bottom on any content-producing key.
function _ebizCreateScrollKeyDownHandler(root) {
    const SKIP_KEYS = new Set(["Control", "Alt", "Shift", "Meta", "CapsLock", "Tab", "Escape"]);
    return function (e) {
        if (SKIP_KEYS.has(e.key)) return;
        const ms = root.querySelector(".ebiz-multi-select-dropdown");
        if (ms) requestAnimationFrame(() => { ms.scrollTop = ms.scrollHeight; });
    };
}

// Attaches the Enter (capture) and scroll (bubble) keydown handlers to the filter input.
// Intercepts Enter before Telerik processes it (AllowCustom="false") to invoke tag creation.
function _ebizAttachFilterInputHandlers(root, id) {
    const filterInput = root.querySelector("input#ebiz-multiselect-dropdown");
    if (!filterInput) return;

    _ebizAttachListener(filterInput, "keydown", _ebizCreateEnterKeyDownHandler(id, root), "_ebizEnterKeyDownHandler", true);
    _ebizAttachListener(filterInput, "keydown", _ebizCreateScrollKeyDownHandler(root), "_ebizScrollKeyDownHandler");

    globalThis.multiselect.log("[AddOnClickHandler] filter input keydown handlers attached for", id);
}

// ===== End of AddOnClickHandler helpers =====

globalThis.ebizMultiSelect.AddOnClickHandler = function (id) {
    const root = document.getElementById(id);
    if (!root) {
        globalThis.multiselect.log("[AddOnClickHandler] MultiSelect not found.", id);
        return;
    }

    // Prevent mousedown on .k-input-values whitespace gaps and the label container from
    // focusing the hidden input — chips already handle this via @onmousedown:preventDefault.
    _ebizAttachMouseDownPrevention(root.querySelector(".k-input-values"), "_ebizInputValuesMouseDownHandler");
    _ebizAttachMouseDownPrevention(root.querySelector(".ebiz-multi-select-label-class"), "_ebizLabelClassMouseDownHandler");
    _ebizAttachFilterInputHandlers(root, id);

    globalThis.multiselect.log("[AddOnClickHandler] handlers attached for", id);
};

globalThis.ebizMultiSelect.scrollToTop = function (id) {
    requestAnimationFrame(() => {
        const root = document.getElementById(id);
        if (!root) return;
        const ms = root.querySelector(".ebiz-multi-select-dropdown");
        if (!ms) return;
        ms.scrollTop = 0;
    });
};

globalThis.ebizMultiSelect.scrollToBottom = function (id) {
    requestAnimationFrame(() => {
        const root = document.getElementById(id);
        if (!root) return;
        const ms = root.querySelector(".ebiz-multi-select-dropdown");
        if (!ms) return;
        ms.scrollTop = ms.scrollHeight;
    });
};

// afterLayout is used to make sure DOM is fully rendered before measuring width
globalThis.multiselect.afterLayout = async function () {

    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => setTimeout(resolve, 0));
};

// ===== Badge width helpers =====

// Returns the usable content width (in px) of the .k-input-values tag area for the given host.
// Subtracts horizontal padding so the result matches the layout budget for badge rows.
globalThis.multiselect.getInputValuesWidth = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) return 0;

    const inputValues = host.querySelector(".k-input-values");
    if (!inputValues) return 0;

    const rect = inputValues.getBoundingClientRect();
    const style = getComputedStyle(inputValues);
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;

    return Math.max(0, rect.width - paddingLeft - paddingRight);
};

// Sets the --ebiz-badge-max-width CSS variable on both the component host element and its
// Telerik portal popup so proportional badge text truncation applies everywhere.
globalThis.multiselect.setBadgeTextMaxWidth = function (hostId, maxWidthPx) {
    const host = document.getElementById(hostId);
    if (host) {
        host.style.setProperty('--ebiz-badge-max-width', maxWidthPx + 'px');
    }

    const popup = document.querySelector(`.ebiz-popup-${hostId}`);
    if (popup) {
        popup.style.setProperty('--ebiz-badge-max-width', maxWidthPx + 'px');
    }
};

globalThis.multiselect.getBadgeWidthsByKey = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) return [];

    const els = host.querySelectorAll(".tagBadgeMeasure[data-key]");
    const results = [];

    els.forEach(el => {
        const key = el.dataset.key;
        if (!key) return;

        const rect = el.getBoundingClientRect();
        const st = getComputedStyle(el);

        const width =
            rect.width +
            (Number.parseFloat(st.marginLeft || "0") || 0) +
            (Number.parseFloat(st.marginRight || "0") || 0);

        results.push({ key, width });
    });

    globalThis.multiselect.log("[getBadgeWidthsByKey]", results);
    return results;
};

// Keyboard event to escape modal
globalThis.ebizMultiSelectModalEsc = globalThis.ebizMultiSelectModalEsc || {
    _handler: null,
    _dotNetRef: null,

    register(dotNetRef) {
        this._dotNetRef = dotNetRef;

        if (this._handler) return;

        this._handler = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();

                try {
                     this._dotNetRef.invokeMethodAsync("OnModalEscape");
                } catch (err) {
                    globalThis.multiselect.log("[ModalEsc] invoke failed", err);
                }
            }
        };

        document.addEventListener("keydown", this._handler, true);
        globalThis.multiselect.log("[ModalEsc] registered");
    },

    unregister() {
        if (!this._handler) return;

        document.removeEventListener("keydown", this._handler, true);
        this._handler = null;
        this._dotNetRef = null;
        globalThis.multiselect.log("[ModalEsc] unregistered");
    }
};

// Negate native 'Escape' key press when multiselect dropdown is opened
globalThis.ebizMultiSelectEscBlocker = globalThis.ebizMultiSelectEscBlocker || {
    _handler: null,
    _enabled: false,

    enable() {
        if (this._enabled) return;
        this._enabled = true;

        this._handler = (e) => {
            if (e.key !== "Escape") return;

            const active = document.activeElement;
            const inEditMode = !!active && !!active.closest(".edit-mode-container");

            // If focus is inside the edit textbox/container, let Escape through
            if (inEditMode) {
                globalThis.multiselect.log("[EscBlocker] edit mode active, letting Escape through");
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            globalThis.multiselect.log("[EscBlocker] Escape blocked (dropdown open)");
        };

        document.addEventListener("keydown", this._handler, true);
        globalThis.multiselect.log("[EscBlocker] enabled");
    },

    disable() {
        if (!this._enabled) return;
        this._enabled = false;

        document.removeEventListener("keydown", this._handler, true);
        this._handler = null;
        globalThis.multiselect.log("[EscBlocker] disabled");
    }
};

// Watches the Telerik popup's animation container for the .k-animation-container-shown
// class. The class is the canonical "popup is alive" marker — Kendo/Telerik keeps it set
// across top↔bottom reposition flips and removes it only on a real close (including
// scroll-induced closes that don't fire OnClose). Using MutationObserver instead of
// polling means no timing heuristics and no false fires during reposition animations.
globalThis.ebizMultiSelectPopupWatch = globalThis.ebizMultiSelectPopupWatch || {
    _observers: new Map(),
    _attachTimers: new Map(),
    _closeTimers: new Map(),

    start(hostId, dotNetRef) {
        this.stop(hostId);

        const queryPopup = () => document.querySelector(`.ebiz-popup-${hostId}`);

        const isPopupShown = (el) =>
            !!el?.isConnected &&
            el.classList.contains("k-animation-container-shown") &&
            getComputedStyle(el).display !== "none";

        // Wait for all active CSS animations/transitions on the popup tree to finish.
        // Each Animation's .finished promise resolves when the animation actually completes,
        // so this stretches naturally under CPU throttle instead of relying on a fixed timeout.
        // Re-queries getAnimations after each batch in case a new animation starts mid-wait
        // (e.g. reposition kicks off a second transition right after the first ends).
        // Hard cap at 2s as a safety valve against pathological infinite animations.
        const waitForAnimationsToSettle = async (el) => {
            if (!el?.isConnected || typeof el.getAnimations !== "function") return;
            const deadline = performance.now() + 2000;
            while (performance.now() < deadline) {
                const anims = el.getAnimations({ subtree: true });
                if (anims.length === 0) return;
                const remaining = deadline - performance.now();
                await Promise.race([
                    Promise.allSettled(anims.map(a => a.finished)),
                    new Promise(r => setTimeout(r, Math.max(0, remaining)))
                ]);
            }
        };

        const fireClose = () => {
            const existing = this._closeTimers.get(hostId);
            if (existing) clearTimeout(existing);

            // Short debounce coalesces rapid attribute mutations during a reposition flip.
            // Under throttle, the real wait happens after this in waitForAnimationsToSettle.
            const timer = setTimeout(async () => {
                this._closeTimers.delete(hostId);

                // First check — if the popup is already back, this was a reposition.
                if (isPopupShown(queryPopup())) return;

                // Wait for any running animations to complete before deciding. This is the
                // CPU-throttle-safe equivalent of the old initial-settling grace window —
                // we wait for actual completion rather than a wall-clock timeout.
                await waitForAnimationsToSettle(queryPopup());

                // Re-check after animations settle. If the popup came back during the wait
                // (reposition completed and re-shown), bail out.
                if (isPopupShown(queryPopup())) return;

                try {
                    await dotNetRef.invokeMethodAsync("OnPopupClosedFromJs");
                } catch (err) {
                    globalThis.multiselect.log("[PopupWatch] invoke failed", err);
                }

                this.stop(hostId);
            }, 100);

            this._closeTimers.set(hostId, timer);
        };

        const cancelClose = () => {
            const existing = this._closeTimers.get(hostId);
            if (existing) {
                clearTimeout(existing);
                this._closeTimers.delete(hostId);
            }
        };

        const attach = () => {
            const root = document.getElementById(hostId);
            if (!root?.isConnected) {
                this.stop(hostId);
                return;
            }

            const popup = document.querySelector(`.ebiz-popup-${hostId}`);
            if (!popup) {
                // Telerik renders the portal popup async — retry next frame.
                const id = requestAnimationFrame(attach);
                this._attachTimers.set(hostId, id);
                return;
            }

            let lastShown = isPopupShown(popup);

            // Watch attribute changes on the popup itself (class for the shown marker, style
            // for inline display toggles).
            const attrObserver = new MutationObserver(() => {
                const nowShown = isPopupShown(popup);
                if (lastShown && !nowShown) fireClose();
                else if (nowShown) cancelClose();
                lastShown = nowShown;
            });
            attrObserver.observe(popup, {
                attributes: true,
                attributeFilter: ["class", "style"]
            });

            // Watch the parent for childList changes — if Telerik tears down the popup
            // element entirely (e.g. on scroll-close), attribute observers on the detached
            // popup go silent, so we need a separate observer on its parent.
            let childObserver = null;
            const parent = popup.parentNode;
            if (parent) {
                childObserver = new MutationObserver(() => {
                    if (!popup.isConnected) {
                        lastShown = false;
                        fireClose();
                    }
                });
                childObserver.observe(parent, { childList: true });
            }

            this._observers.set(hostId, { attrObserver, childObserver });
            globalThis.multiselect.log("[PopupWatch] observer attached for", hostId);
        };

        attach();
    },

    stop(hostId) {
        const observers = this._observers.get(hostId);
        if (observers) {
            observers.attrObserver?.disconnect();
            observers.childObserver?.disconnect();
            this._observers.delete(hostId);
        }

        const attachTimer = this._attachTimers.get(hostId);
        if (attachTimer) {
            cancelAnimationFrame(attachTimer);
            this._attachTimers.delete(hostId);
        }

        const closeTimer = this._closeTimers.get(hostId);
        if (closeTimer) {
            clearTimeout(closeTimer);
            this._closeTimers.delete(hostId);
        }

        globalThis.multiselect.log("[PopupWatch] stopped for", hostId);
    }
};

// Blur to lose focus when scrolling down occurs, to prevent unwanted keyboard interactions with the dropdown when focus is lost due to scroll
globalThis.ebizMultiSelect.forceLoseFocus = function (hostId) {
    const host = document.getElementById(hostId);
    if (!host) return;

    const active = document.activeElement;
    if (!active) return;

    // Only act if focus is inside this multiselect
    if (!host.contains(active)) return;

    const body = document.body;
    const hadTabIndex = body.hasAttribute("tabindex");
    const previousTabIndex = body.getAttribute("tabindex");

    // Makes body focusable to shift the focus
    if (!hadTabIndex) {
        body.setAttribute("tabindex", "-1");
    }

    body.focus({ preventScroll: true });

    // Remove DOM changes
    if (!hadTabIndex) {
        body.removeAttribute("tabindex");
    } else if (previousTabIndex !== null) {
        body.setAttribute("tabindex", previousTabIndex);
    }

    globalThis.multiselect?.log?.("[forceLoseFocus] focus moved to body for", hostId);
};